import { FundingOptionItem } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import FundingRadioBtnSelection from 'features/onboarding/components/FundingRadioBtnSelection/FundingRadioBtnSelection';
import { getIllustrationForFunding } from 'features/onboarding/utils/getIllustrationForFunding';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isAndroid } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import theme from 'shared/styles/theme';

interface FundingSelectionScreenProps {
  optionsData: FundingOptionItem[];
  headlineText: string;
  onContinueButton: (
    selectedOption: string | undefined,
    didSkip?: boolean,
  ) => void;
  skipButton?: {
    text?: string;
    clickAnalytics: {
      name: string;
      properties: { key: string; value: string }[];
    };
  };
  handleBackButtonAction?: () => void;
}

const FundingSelectionScreen = ({
  optionsData,
  headlineText,
  onContinueButton,
  skipButton = undefined,
  handleBackButtonAction,
}: FundingSelectionScreenProps) => {
  const { bottom } = useSafeAreaInsets();
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    undefined,
  );

  const { segmentClient } = useAnalyticsConfiguration();

  const { t: translatedText } = useTranslation('onboarding');

  const containerContentStyle = {
    paddingTop: 0,
  };

  return (
    <View style={styles.container}>
      {skipButton?.text ? (
        <NavigationBar
          title=""
          trailingAccessories={
            <Button
              label={skipButton.text}
              size="SMALL"
              type="TEXT"
              width="HUG"
              onPress={() => {
                const { name, properties } = parseGQLAnalyticsEvent(
                  skipButton.clickAnalytics,
                );
                void segmentClient.logEvent(name, properties);
                onContinueButton(undefined, true);
              }}
            />
          }
        />
      ) : (
        <Stack.Screen
          options={{
            headerShown: true,
            headerBackVisible: true,
            title: '',
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                {...props}
                primaryOnPress={handleBackButtonAction}
              />
            ),
          }}
        />
      )}
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={containerContentStyle}
        >
          {headlineText ? (
            <TitleXLText style={styles.headlineText}>
              {headlineText}
            </TitleXLText>
          ) : null}
          <View style={styles.radioCardsContainer}>
            {optionsData.map((option: FundingOptionItem) => (
              <FundingRadioBtnSelection
                key={option.type}
                isSelected={option.type === selectedOption}
                title={option.title}
                illustration={
                  getIllustrationForFunding(option.type) as
                    IllustrationKeyType | IconKeyType
                }
                tags={option.tags || undefined}
                id={option.type}
                onPress={() => {
                  setSelectedOption(option.type);
                }}
              />
            ))}
          </View>
        </ScrollView>
        <View
          style={[
            styles.footerContainer,
            {
              paddingBottom: isAndroid
                ? bottom + theme.spacing.m
                : bottom + theme.spacing.l,
            },
          ]}
        >
          <Button
            label={translatedText('onboardingOptionsScreen.continue')}
            isDisabled={!selectedOption}
            onPress={() => {
              const selectedOptionData = optionsData.find(
                (opt) => opt.type === selectedOption,
              );
              if (selectedOptionData?.clickAnalytics) {
                const { name, properties } = parseGQLAnalyticsEvent(
                  selectedOptionData.clickAnalytics,
                );
                void segmentClient.logEvent(name, properties);
              }
              onContinueButton(selectedOption);
            }}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primary,
    borderTopWidth: 1,
    padding: theme.spacing.m,
  },
  headlineText: { padding: theme.spacing.m, textAlign: 'left' },
  radioCardsContainer: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  scrollContainer: {
    flex: 1,
  },
});

export default FundingSelectionScreen;
